<template>
    <div class="currency-select">
        <div class="info">
            <div class="d-flex justify-content-center align-items-center flex-column">
                <div class="w-100">
                    <div class="d-flex justify-content-center align-items-center position-relative mb-2">
                        <SearchIcon />
                        <input v-model="search"
                               :placeholder="getTrans('header.search', 'Search')"
                               class="mb-0"
                               @click.stop >
                    </div>
                    <Loader v-if="!currencyConverterStore.isCryptoCurrenciesDataFetchCompleted"
                            :variant="LoaderVariantsEnum.WHITE"
                            class="crypto-currency-loader"/>
                    <div v-else>
                        <div v-if="!converterModalStore.showOnlyCrypto"
                             class="d-flex flex-row align-items-center currency-view-selector">
                            <button v-for="(tab, index) in tabs"
                                    :key="tab.name"
                                    class="d-flex justify-content-center align-items-center btn w-50"
                                    :class="{ active: tab.isActive }"
                                    @click.stop="changeActiveTab(index)">
                                <span>
                                    <Translate :translationKey="`header.${tab.name}`"
                                               :fallback="tab.fallBack" />
                                </span>
                            </button>
                        </div>
                        <div v-if="tabs[0].isActive || converterModalStore.showOnlyCrypto">
                            <Loader v-if="searchLoading"
                                    :variant="LoaderVariantsEnum.WHITE"
                                    class="currency-search-loader"/>
                            <div v-else>
                                <div v-show="filteredCryptoCurrencies.length > 0">
                                    <div ref="cryptoCurrenciesContainer"
                                         class="currencies-container">
                                        <div v-for="cryptoCurrency in filteredCryptoCurrencies"
                                             :key="cryptoCurrency.name"
                                             class="currencies"
                                             @click.stop="changeCryptoCurrency(cryptoCurrency)">
                                            <div class="currency-name">
                                                <img class="mr-2"
                                                     :src="asset_cdn(cryptoCurrency.logo, 32)"
                                                     :alt="cryptoCurrency.name"
                                                     :title="cryptoCurrency.name">
                                                <span class="currency-name-text">
                                                    {{ cryptoCurrency.name }}
                                                </span>
                                            </div>
                                            <div class="currency-code text-nowrap">
                                                {{ cryptoCurrency.currency_code }}
                                            </div>
                                        </div>
                                        <template v-if="!isAllCryptoCurrenciesLoaded">
                                            <div v-if="isInfiniteLoading"
                                                 class="currency-infinite-loader d-flex align-items-center justify-content-center">
                                                <Loader :variant="LoaderVariantsEnum.WHITE"
                                                        class="mr-2"/>
                                                <Translate translationKey="cryptocurrencies.loading"
                                                           fallback="Loading..." />
                                            </div>
                                            <div v-else
                                                 class="load-more-button">
                                                <BaseButton :variant="ButtonVariantEnum.QUATERNARY"
                                                            @click="loadMoreCryptoCurrencies">
                                                    <Translate translationKey="quests-dashboard.loadMore"
                                                               fallback="Load More" />
                                                </BaseButton>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div v-if="!filteredCryptoCurrencies || currencyConverterStore.cryptoCurrencies?.length < 1"
                                     class="no-results-message">
                                    <Translate translationKey="header.searchNoResults"
                                               fallback="No results found. Try a different search query." />
                                </div>
                            </div>
                        </div>
                        <div v-if="tabs[1].isActive && !converterModalStore.showOnlyCrypto">
                            <div v-if="filteredFiatCurrencies.length > 0">
                                <div class="currencies-container">
                                    <div v-for="fiatCurrency in filteredFiatCurrencies"
                                         :key="fiatCurrency.name"
                                         class="currencies"
                                         @click.stop="changeFiatCurrency(fiatCurrency)">
                                        <div class="currency-name">
                                            <img class="mr-2"
                                                 :src="asset_cdn(fiatCurrency.logo, 32)"
                                                 :alt="fiatCurrency.name"
                                                 :title="fiatCurrency.name">
                                            <span class="currency-name-text">
                                                {{ fiatCurrency.name }}
                                            </span>
                                        </div>
                                        <div class="currency-code text-nowrap">
                                            {{ fiatCurrency.currency_code }} - {{ fiatCurrency.sign }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else
                                 class="no-results-message">
                                <Translate translationKey="header.searchNoResults"
                                           fallback="No results found. Try a different search query." />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed, ref, onMounted, watch,
    } from 'vue';
    import { getTrans } from '@/js/helpers/getTrans';
    import { asset_cdn } from '@/js/helpers/helpers';
    import SearchIcon from '@/js/components/icons/SearchIcon.vue';
    import { useConverterModalStore } from '@/js/store/currency-converter/ConverterModalStore';
    import { useCurrencyConverterStore } from '@/js/store/currency-converter/CurrencyConverterStore';
    import { FiatCurrency } from '@/js/cryptocurrencies/types/fiatCurrency';
    import { debounce } from '@/js/pages/helpers/debounce';
    import Loader from '@/js/components/Loader.vue';
    import { LoaderVariantsEnum } from '@/js/enums/loaderVariants';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';
    import BaseButton from '@/js/components/BaseButton.vue';

    interface IFiatCurrencySearchProps {
        fiatCurrencies: Array<FiatCurrency>;
        currentFiatCurrency: FiatCurrency;
        changeCurrencyProp?: (selectedFiatCurrency) => void;
        changeCryptoCurrencyProp?: (selectedCryptoCurrency) => void;
    }

    const props = withDefaults(defineProps<IFiatCurrencySearchProps>(), {
        changeCurrencyProp: () => {},
        changeCryptoCurrencyProp: () => {},
    });

    const converterModalStore = useConverterModalStore();
    const currencyConverterStore = useCurrencyConverterStore();

    const search = ref('');
    const tabs = ref([
        {
            name: 'crypto',
            fallBack: 'Crypto Currency',
            isActive: converterModalStore.showOnlyCrypto,
        },
        {
            name: 'fiatCurrency',
            fallBack: 'Fiat Currency',
            isActive: !converterModalStore.showOnlyCrypto,
        },
    ]);

    const cryptoCurrenciesContainer = ref(null);
    const isInfiniteLoading = ref(false);
    const isAllCryptoCurrenciesLoaded = ref(false);

    const changeActiveTab = async (selectedIndex) => {
        search.value = '';
        tabs.value.forEach((tab, index) => {
            tab.isActive = (index === selectedIndex);
        });

        if (tabs.value[0].isActive) {
            await currencyConverterStore.fetchCryptoCurrencies({ page: 1, perPage: 20 });
            isAllCryptoCurrenciesLoaded.value = currencyConverterStore.cryptoCurrencies.length >= currencyConverterStore.totalItems;
        }
    };

    const filteredFiatCurrencies = computed((): Array<FiatCurrency> => {
        const searchLower = search.value.toLowerCase();

        return props.fiatCurrencies.filter((fiatCurrency: FiatCurrency) => {
            const nameLowercase = fiatCurrency.name.toLowerCase();
            const currencyCodeLowercase = fiatCurrency.currency_code.toLowerCase();
            const signLowercase = fiatCurrency.sign?.toLowerCase() || '';

            return (
                nameLowercase.includes(searchLower)
                || currencyCodeLowercase.includes(searchLower)
                || signLowercase.includes(searchLower)
            );
        });
    });

    const filteredCryptoCurrencies = computed(() => {
        if (!currencyConverterStore.cryptoCurrencies) {
            return [];
        }

        const searchLower = search.value.toLowerCase();

        return currencyConverterStore.cryptoCurrencies.filter((crypto) => {
            const nameLowercase = crypto.name.toLowerCase();
            const symbolLowercase = crypto.currency_code.toLowerCase();

            return nameLowercase.includes(searchLower) || symbolLowercase.includes(searchLower);
        });
    });

    const closeModal = () => {
        converterModalStore.setConverterCurrencyModal(false);
    };

    const changeFiatCurrency = (fiatCurrency: FiatCurrency) => {
        if (props.changeCurrencyProp) {
            props.changeCurrencyProp(fiatCurrency);
        }

        closeModal();
    };

    const changeCryptoCurrency = (cryptoCurrency) => {
        if (props.changeCryptoCurrencyProp) {
            if (converterModalStore.showOnlyCrypto) {
                props.changeCryptoCurrencyProp(cryptoCurrency);
            } else {
                props.changeCurrencyProp(cryptoCurrency);
            }
        }

        closeModal();
    };

    const loadMoreCryptoCurrencies = () => {
        if (!currencyConverterStore.loading) {
            isInfiniteLoading.value = true;

            const nextPage = currencyConverterStore.page + 1;
            const searchValue = search.value;

            currencyConverterStore.fetchCryptoCurrencies({
                search: searchValue,
                page: nextPage,
                perPage: 20,
            }).finally(() => {
                isInfiniteLoading.value = false;
                isAllCryptoCurrenciesLoaded.value = currencyConverterStore.cryptoCurrencies.length >= currencyConverterStore.totalItems;
            });
        }
    };

    const searchLoading = ref(false);

    const debouncedFetchCryptoCurrencies = debounce((searchValue: string) => {
        searchLoading.value = true;
        currencyConverterStore.fetchCryptoCurrencies({ search: searchValue, page: 1, perPage: 20 })
            .finally(() => {
                searchLoading.value = false;
                isAllCryptoCurrenciesLoaded.value = currencyConverterStore.cryptoCurrencies.length >= currencyConverterStore.totalItems;
            });
    }, 300);

    watch(search, async (newSearch) => {
        if (tabs.value[0].isActive) {
            await debouncedFetchCryptoCurrencies(newSearch);
        }
    });

    onMounted(async () => {
        await currencyConverterStore.fetchCryptoCurrencies({ page: 1, perPage: 30 });
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/partials/mixins" as mixins;
@use "@/sass/mixins/_scrollbar" as scrollbarMixins;

.currency-select {
    width: 100%;
    height: auto;
    min-height: 350px;

    .load-more-button {
        button {
            margin: 16px auto 0;
            font-size: 13px;
        }
    }

    .crypto-currency-loader,
    .currency-search-loader,
    .no-results-message {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .currency-infinite-loader {
        color: variables.$baseWhite;
        font-size: 14px;
        font-weight: 400;
    }

    .currencies-container {
        @include scrollbarMixins.simple-scrollbar;
        & {
            height: 325px;
            max-height: 325px;
            padding-right: 12px;
            overflow-y: scroll;
        }

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            height: calc(100dvh - 180px);
            max-height: calc(100dvh - 180px);
        }
    }

    .info {
        width: 100%;

        .currency-view-selector {
            display: flex;
            padding: 3px;
            border: 1px solid variables.$blue800;
            border-radius: 8px;
            margin-bottom: 8px;
            background-color: variables.$brand950;

            @media (max-width: breakpoints.$sm-breakpoint-max-width) {
                padding: 6px;
            }

            button:not(:last-child) {
                margin-right: 4px;
            }

            .active {
                background-color: variables.$blue800;
            }

            .btn {
                width: 50%;
                justify-content: center;
                padding: 5px 10px;
                border-radius: 6px;
                color: variables.$baseWhite;

                @media (max-width: breakpoints.$sm-breakpoint-max-width) {
                    padding: 10px;
                }

                &:hover,
                &:active {
                    background-color: variables.$blue800;
                }

                span {
                    color: variables.$baseWhite;
                    font-size: 12px;
                    font-weight: 500;
                }
            }
        }
    }

    .currencies {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;
        padding: 5px 8px;
        border-radius: 15px;
        color: variables.$baseWhite;
        cursor: pointer;

        &.active,
        &:hover {
            background-color: variables.$brand950;
        }

        &:not(:last-child) {
            margin-bottom: 2px;

            @media (max-width: breakpoints.$sm-breakpoint-max-width) {
                margin-bottom: 6px;
            }
        }

        .currency-name {
            display: flex;
            height: 100%;
            align-items: center;
            justify-content: center;
            margin-right: 8px;

            .currency-name-text {
                @include mixins.custom-line-clamp(1);
                font-size: 17px;
                font-weight: 400;
                line-height: 20px;
            }

            img {
                height: 20px;
                border: 1px solid variables.$blue200;
                border-radius: 50%;
                background-color: variables.$blue200;
            }
        }

        .currency-code {
            display: flex;
            align-items: center;
            color: variables.$baseWhite;
            font-size: 14px;
        }
    }

    .search-icon {
        position: absolute;
        left: 9px;
        stroke: variables.$baseWhite;
    }

    .no-results-message {
        width: 100%;
        padding-top: 10px;
        color: variables.$baseWhite;
        font-size: 13px;
        font-weight: 400;
        line-height: 16px;
        text-align: center;
    }

    input {
        width: 100%;
        padding: 6px 8px 6px 28px;
        border: 1px solid variables.$blue950;
        border-radius: 7px;
        margin-bottom: 8px;
        background-color: variables.$brand950;
        color: variables.$baseWhite;
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        outline: 1px solid variables.$blue950;
        transition: all 0.5s ease-in-out;

        @media (max-width: breakpoints.$sm-breakpoint-max-width) {
            line-height: 32px;
        }

        &::placeholder {
            color: variables.$gray500;
            opacity: 1;
        }

        &:focus-visible {
            background-color: variables.$brand950;
            outline: 1px solid variables.$blue950;
        }
    }

    .currencies .currency-name img {
        width: 20px;
    }

    .currencies:hover {
        text-decoration: none;
    }
}
</style>
